import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import moment from 'moment';
import 'moment/locale/ru';

import {
    GET_ONE,
    GET_LIST,
    UPDATE,
    provider,
    history,
} from '../../_utils';
import { SearchClear } from '../../_Components';
import Utils from '../utils';
import '../operatorCard.css';

const useIsNarrow = () => {
    const [narrow, setNarrow] = useState(() => (
        typeof window !== 'undefined' && window.matchMedia('(max-width: 860px)').matches
    ));
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 860px)');
        const onChange = (event) => setNarrow(event.matches);
        if (mq.addEventListener) {
            mq.addEventListener('change', onChange);
        } else {
            mq.addListener(onChange);
        }
        setNarrow(mq.matches);
        return () => {
            if (mq.removeEventListener) {
                mq.removeEventListener('change', onChange);
            } else {
                mq.removeListener(onChange);
            }
        };
    }, []);
    return narrow;
};

const Icon = ({ d, size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d={d} />
    </svg>
);

const ArrowIn = () => (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9">
        <path d="M11 5L5 11M5 6.5V11h4.5" />
    </svg>
);

const ArrowOut = () => (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9">
        <path d="M5 11l6-6M6.5 5H11v4.5" />
    </svg>
);

const num = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const formatTime = (value) => {
    if (!value) return '';
    return moment(value).isValid() ? moment(value).format('HH:mm') : String(value);
};

const initials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '—';
    return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
};

const liveTime = (seconds) => {
    const total = num(seconds);
    if (total >= 3600) return Utils.secondsToTime(total);
    const mm = String(Math.floor(total / 60)).padStart(2, '0');
    const ss = String(total % 60).padStart(2, '0');
    return `${mm}:${ss}`;
};

const sortQueues = (list) => (
    [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'))
);

const idsKey = (list) => list.map((row) => row.id).sort().join(',');

const pct = (part, total) => (total ? Math.round((part / total) * 1000) / 10 : 0);

const QueueColumn = ({
    title,
    count,
    query,
    onQuery,
    onSelectAll,
    items,
    selected,
    onToggle,
    onMove,
}) => (
    <div className="pc-op-qcol">
        <div className="pc-op-qh">
            <span className="t">{title}</span>
            <span className="n">{count}</span>
        </div>
        <div className="pc-op-qs">
            <div className="pc-op-srch">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <circle cx="7" cy="7" r="4.5" />
                    <path d="M10.5 10.5L14 14" />
                </svg>
                <input className="pc-op-inp" value={query} placeholder="Поиск" onChange={(e) => onQuery(e.target.value)} />
                <SearchClear visible={Boolean(query)} onClear={() => onQuery('')} />
            </div>
            <button type="button" className="pc-op-btn pc-op-btn--sm" onClick={onSelectAll}>Все</button>
        </div>
        <div className="pc-op-qlist">
            {items.length === 0 ? (
                <div className="pc-op-qempty">{query ? 'Ничего не найдено' : 'Список пуст'}</div>
            ) : items.map((queue) => {
                const on = selected.has(queue.id);
                return (
                    <button
                        type="button"
                        key={queue.id}
                        className={`pc-op-q${on ? ' sel' : ''}`}
                        onClick={() => onToggle(queue.id)}
                        onDoubleClick={() => onMove(queue)}
                    >
                        <input type="checkbox" checked={on} readOnly />
                        <span className="nm">{queue.name}</span>
                        <i>{queue.id}</i>
                    </button>
                );
            })}
        </div>
    </div>
);

const OperatorCardModal = ({ open, agent, onClose }) => {
    const narrow = useIsNarrow();
    const [item, setItem] = useState(null);
    const [operatorId, setOperatorId] = useState(null);
    const [start, setStart] = useState('09:00');
    const [end, setEnd] = useState('18:00');
    const [available, setAvailable] = useState([]);
    const [assigned, setAssigned] = useState([]);
    const [initialAssigned, setInitialAssigned] = useState('');
    const [selA, setSelA] = useState(() => new Set());
    const [selB, setSelB] = useState(() => new Set());
    const [qA, setQA] = useState('');
    const [qB, setQB] = useState('');
    const [saving, setSaving] = useState(false);

    const agentId = agent?.id;
    const agentLogin = agent?.login;
    const systemId = agent?.systemId;

    useEffect(() => {
        if (!open || !agentId) return undefined;
        let cancelled = false;

        setOperatorId(systemId || null);

        const load = async () => {
            try {
                const [{ data: agentData }, { data: agentInfo }, queuesResult, operatorsResult] = await Promise.all([
                    provider(GET_ONE, '/dashboard/agent', agentId),
                    provider(GET_ONE, '/infra/agent', agentId),
                    provider(GET_LIST, '/infra/queues', {
                        filter: {},
                        sort: { field: 'name', order: 'ASC' },
                        pagination: { page: 1, perPage: 1000 },
                    }),
                    systemId
                        ? Promise.resolve({ data: [] })
                        : provider(GET_LIST, '/operators', {
                            filter: { login: agentLogin },
                            pagination: { page: 1, perPage: 1 },
                        }).catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;

                const detail = agentData || {};
                setItem(detail);
                setStart(formatTime(detail.schedule?.start) || '09:00');
                setEnd(formatTime(detail.schedule?.end) || '18:00');

                const rawQueues = agentInfo?.Queues || detail.queues || [];
                const assignedIds = new Set(rawQueues.map((queue) => (
                    queue && typeof queue === 'object' ? queue.id : queue
                )));
                const queues = queuesResult.data || [];
                const left = [];
                const right = [];
                queues.forEach((queue) => {
                    if (assignedIds.has(queue.id)) right.push(queue);
                    else left.push(queue);
                });
                const sortedRight = sortQueues(right);
                setAvailable(sortQueues(left));
                setAssigned(sortedRight);
                setInitialAssigned(idsKey(sortedRight));
                setSelA(new Set());
                setSelB(new Set());
                setQA('');
                setQB('');

                if (systemId) {
                    setOperatorId(systemId);
                } else {
                    const found = (operatorsResult.data || [])[0];
                    setOperatorId(found?._id || found?.id || null);
                }
            } catch (error) {
                console.error('Error loading operator card:', error);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [open, agentId, agentLogin, systemId]);

    const stats = item?.statistics || item?.statistic || {};
    const dirty = idsKey(assigned) !== initialAssigned;

    const activity = useMemo(() => {
        const talk = num(stats.talkTime);
        const paid = num(stats.paid) || num(stats.ut);
        const ready = Math.max(0, paid - talk);
        const rest = num(stats.rest);
        const nr = num(stats.notReady);
        const worked = num(stats.totalTime) || (talk + ready + rest + nr);
        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        const shiftSec = Math.max(0, ((eh * 60 + em) - (sh * 60 + sm)) * 60);
        const online = paid || (talk + ready);
        const off = Math.max(0, worked - online - rest - nr);
        const rows = [
            { k: 'talk', n: 'Разговор', s: talk, c: 'pc-op-s-talk' },
            { k: 'ready', n: 'Готов, ждёт звонка', s: ready, c: 'pc-op-s-ready' },
            { k: 'rest', n: 'Перерыв', s: rest, c: 'pc-op-s-rest' },
            { k: 'nr', n: 'Не готов', s: nr, c: 'pc-op-s-nr' },
            { k: 'off', n: 'Не на линии', s: off, c: 'pc-op-s-off' },
        ];
        const tracked = rows.reduce((sum, row) => sum + row.s, 0);
        return { rows, shiftSec, online, talk, paid, worked, tracked };
    }, [stats.talkTime, stats.paid, stats.ut, stats.rest, stats.notReady, stats.totalTime, start, end]);

    const occ = activity.paid
        ? (activity.talk / activity.paid) * 100
        : num(stats.occPercent);
    const ut = activity.worked
        ? (activity.paid / activity.worked) * 100
        : (activity.shiftSec ? (activity.paid / activity.shiftSec) * 100 : num(stats.utPercent));
    const shiftHours = activity.shiftSec / 3600;
    const shiftHoursLabel = Number.isInteger(shiftHours)
        ? `${shiftHours} ч`
        : `${String(shiftHours.toFixed(1)).replace('.', ',')} ч`;

    const inbound = num(stats.inbound);
    const outbound = num(stats.outbound);
    const abandoned = num(stats.abandoned);
    const inboundTime = num(stats.inboundTime);
    const outboundTime = num(stats.outboundTime);
    const allCalls = num(stats.calls) || inbound + outbound;
    const allTime = num(stats.talkTime) || inboundTime + outboundTime;
    const avg = (count, seconds) => (count ? seconds / count : 0);

    const talking = agent?.talking > 0;
    const session = agent?.sessions?.[0];
    const liveSeconds = session?.lastStateDuration ?? agent?.stateDuration;
    const status = session
        ? Utils.getSessionState(session.state)
        : (agent?.stateCode ? Utils.getAgentState(agent.stateCode) : {});

    const filteredA = useMemo(() => {
        const q = qA.trim().toLowerCase();
        return q ? available.filter((row) => (row.name || '').toLowerCase().includes(q)) : available;
    }, [available, qA]);

    const filteredB = useMemo(() => {
        const q = qB.trim().toLowerCase();
        return q ? assigned.filter((row) => (row.name || '').toLowerCase().includes(q)) : assigned;
    }, [assigned, qB]);

    const toggleSel = (side, id) => {
        const setter = side === 'a' ? setSelA : setSelB;
        setter((current) => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectVisible = (side) => {
        const list = side === 'a' ? filteredA : filteredB;
        const selected = side === 'a' ? selA : selB;
        const setter = side === 'a' ? setSelA : setSelB;
        const allOn = list.length > 0 && list.every((row) => selected.has(row.id));
        setter(() => {
            const next = new Set(selected);
            list.forEach((row) => (allOn ? next.delete(row.id) : next.add(row.id)));
            return next;
        });
    };

    const moveToAssigned = useCallback((queues) => {
        const moving = new Set(queues.map((row) => row.id));
        setAvailable((current) => current.filter((row) => !moving.has(row.id)));
        setAssigned((current) => sortQueues(current.concat(queues)));
        setSelA((current) => {
            const next = new Set(current);
            moving.forEach((id) => next.delete(id));
            return next;
        });
    }, []);

    const moveToAvailable = useCallback((queues) => {
        const moving = new Set(queues.map((row) => row.id));
        setAssigned((current) => current.filter((row) => !moving.has(row.id)));
        setAvailable((current) => sortQueues(current.concat(queues)));
        setSelB((current) => {
            const next = new Set(current);
            moving.forEach((id) => next.delete(id));
            return next;
        });
    }, []);

    const save = async () => {
        if (!agentId || saving) return;
        setSaving(true);
        try {
            await provider(UPDATE, '/infra/agent/settings', {
                id: agentId,
                data: { queues: assigned.map((row) => row.id) },
            });
            setInitialAssigned(idsKey(assigned));
        } catch (error) {
            console.error('Error saving operator queues:', error);
        } finally {
            setSaving(false);
        }
    };

    const statusChip = talking
        ? { className: 'pc-op-chip pc-op-chip--live', text: <>В разговоре · <span className="pc-op-m">{liveTime(liveSeconds)}</span></>, live: true }
        : status?.value === 'Ready'
            ? { className: 'pc-op-chip pc-op-chip--ok', text: status.label || 'Готов' }
            : status?.value === 'Not Ready'
                ? { className: 'pc-op-chip pc-op-chip--bad', text: status.label || 'Не готов' }
                : status?.value === 'Rest'
                    ? { className: 'pc-op-chip pc-op-chip--warn', text: status.label || 'Отдых' }
                    : { className: 'pc-op-chip', text: status?.label || 'Офлайн' };

    const countA = `${qA ? `${filteredA.length} из ${available.length}` : available.length}${selA.size ? ` · выбрано ${selA.size}` : ''}`;
    const countB = `${qB ? `${filteredB.length} из ${assigned.length}` : assigned.length}${selB.size ? ` · выбрано ${selB.size}` : ''}`;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullScreen={narrow}
            scroll="paper"
            PaperProps={{ className: 'pc-op-modal' }}
        >
            <div className="pc-op">
                <div className="pc-op-mh">
                    <div className="pc-op-ava">{initials(agent?.name)}</div>
                    <div className="pc-op-who">
                        <h2>{agent?.name || 'Оператор'}</h2>
                        <div className="sub">
                            {agent?.login || '—'}
                            {agent?.station ? ` · рабочее место ${agent.station}` : ''}
                        </div>
                    </div>
                    <div className="pc-op-rt">
                        <span className={statusChip.className}>
                            {statusChip.live && <span className="pc-op-dot pc-op-pulse" />}
                            {statusChip.text}
                        </span>
                        <span className="pc-op-chip pc-op-chip--mono">смена {start} – {end} · {shiftHoursLabel}</span>
                        <button
                            type="button"
                            className="pc-op-btn pc-op-btn--sm"
                            onClick={() => history.push('/visor')}
                        >
                            <Icon d="M3 6.5v3M6 4v8M9 5.5v5M12 3v10" />
                            Прослушать
                        </button>
                        <button type="button" className="pc-op-btn pc-op-btn--quiet pc-op-btn--ico" onClick={onClose} aria-label="Закрыть">
                            <Icon d="M4 4l8 8M12 4l-8 8" />
                        </button>
                    </div>
                </div>

                <div className="pc-op-mb">
                    <div className="pc-op-cols">
                        <div>
                            <div className="pc-op-sect">Загрузка · OCC и UT</div>
                            <div className="pc-op-kpis">
                                <div className={`pc-op-kpi${occ < 35 ? ' warn' : ''}`}>
                                    <div className="l">Загрузка · OCC</div>
                                    <div className="v">{occ.toFixed(1).replace('.', ',')}%</div>
                                    <div className="f">разговор ÷ время на линии</div>
                                </div>
                                <div className={`pc-op-kpi${ut < 80 ? ' warn' : ''}`}>
                                    <div className="l">Использование смены · UT</div>
                                    <div className="v">{ut.toFixed(1).replace('.', ',')}%</div>
                                    <div className="f">время на линии ÷ смена</div>
                                </div>
                            </div>

                            <div className="pc-op-sect">Активность за смену</div>
                            <div className="pc-op-stack">
                                {activity.rows.filter((row) => row.s > 0).map((row) => (
                                    <i key={row.k} className={row.c} style={{ flex: row.s }} />
                                ))}
                            </div>
                            {activity.rows.map((row) => {
                                const share = pct(row.s, activity.tracked);
                                return (
                                    <div key={row.k} className={`pc-op-st${row.s ? '' : ' zero'}`}>
                                        <i className={row.c} style={row.s ? undefined : { background: '#DDE3EA' }} />
                                        <span className="n">{row.n}</span>
                                        <span className="t">{Utils.secondsToTime(row.s)}</span>
                                        <span className="pc">{row.s ? `${String(share.toFixed(1)).replace('.', ',')}%` : '—'}</span>
                                    </div>
                                );
                            })}
                        </div>

                        <div>
                            <div className="pc-op-sect">Звонки за смену</div>
                            <div className="pc-op-card">
                                <table className="pc-op-table">
                                    <thead>
                                        <tr>
                                            <th>Направление</th>
                                            <th>Звонков</th>
                                            <th>Общее время</th>
                                            <th>Средний</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <span className="pc-op-dirn">
                                                    <span className="pc-op-arr in"><ArrowIn /></span>
                                                    Входящие
                                                </span>
                                            </td>
                                            <td className="pc-op-m">{inbound}</td>
                                            <td className="pc-op-m">{Utils.secondsToTime(inboundTime)}</td>
                                            <td className="pc-op-m">{Utils.secondsToTime(avg(inbound, inboundTime) || num(stats.inboundAv))}</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <span className="pc-op-dirn">
                                                    <span className="pc-op-arr out"><ArrowOut /></span>
                                                    Исходящие
                                                </span>
                                            </td>
                                            <td className="pc-op-m">{outbound}</td>
                                            <td className="pc-op-m">{Utils.secondsToTime(outboundTime)}</td>
                                            <td className="pc-op-m">{Utils.secondsToTime(avg(outbound, outboundTime) || num(stats.outboundAv))}</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <span className="pc-op-dirn">
                                                    <span className="pc-op-arr miss">
                                                        <Icon d="M4 4l8 8M12 4l-8 8" size={11} />
                                                    </span>
                                                    Отклонённые
                                                </span>
                                            </td>
                                            <td className="pc-op-m">{abandoned}</td>
                                            <td className="pc-op-m">—</td>
                                            <td className="pc-op-m">—</td>
                                        </tr>
                                        <tr className="pc-op-trow">
                                            <td>Всего</td>
                                            <td className="pc-op-m">{allCalls}</td>
                                            <td className="pc-op-m">{Utils.secondsToTime(allTime)}</td>
                                            <td className="pc-op-m">{Utils.secondsToTime(avg(allCalls, allTime))}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="pc-op-sect">
                        Очереди
                        <span className="h">включён в {assigned.length} из {available.length + assigned.length}</span>
                    </div>
                    <div className="pc-op-dual">
                        <QueueColumn
                            title="Доступные"
                            count={countA}
                            query={qA}
                            onQuery={setQA}
                            onSelectAll={() => selectVisible('a')}
                            items={filteredA}
                            selected={selA}
                            onToggle={(id) => toggleSel('a', id)}
                            onMove={(queue) => moveToAssigned([queue])}
                        />
                        <div className="pc-op-mid">
                            <button
                                type="button"
                                title="Включить в очереди"
                                disabled={!selA.size}
                                onClick={() => moveToAssigned(available.filter((row) => selA.has(row.id)))}
                            >
                                <Icon d="M3 8h9M9 4.5L12.5 8 9 11.5" />
                            </button>
                            <button
                                type="button"
                                title="Убрать из очередей"
                                disabled={!selB.size}
                                onClick={() => moveToAvailable(assigned.filter((row) => selB.has(row.id)))}
                            >
                                <Icon d="M13 8H4M7 4.5L3.5 8 7 11.5" />
                            </button>
                        </div>
                        <QueueColumn
                            title="Включён в очереди"
                            count={countB}
                            query={qB}
                            onQuery={setQB}
                            onSelectAll={() => selectVisible('b')}
                            items={filteredB}
                            selected={selB}
                            onToggle={(id) => toggleSel('b', id)}
                            onMove={(queue) => moveToAvailable([queue])}
                        />
                    </div>
                    <div className="pc-op-leg">
                        <span><span className="pc-op-arr in"><ArrowIn /></span>входящая</span>
                        <span><span className="pc-op-arr out"><ArrowOut /></span>исходящая</span>
                        <span style={{ marginLeft: 'auto' }} className="pc-op-leg-hint">Двойной клик по очереди переносит её в соседний список</span>
                    </div>
                </div>

                <div className="pc-op-mf">
                    <button type="button" className="pc-op-btn pc-op-btn--pri" disabled={!dirty || saving} onClick={save}>
                        {saving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                    <button type="button" className="pc-op-btn" onClick={onClose}>Закрыть</button>
                    {dirty && (
                        <span className="pc-op-chip pc-op-chip--bad">
                            <span className="pc-op-dot" />
                            Есть изменения
                        </span>
                    )}
                    <button
                        type="button"
                        className="pc-op-btn pc-op-btn--quiet pc-op-btn--sm"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => operatorId && history.push(`/operators/${operatorId}/show`)}
                        disabled={!operatorId}
                    >
                        <Icon d="M8 3a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM3 13.5c0-2.4 2.2-4 5-4s5 1.6 5 4" />
                        Карточка сотрудника
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default OperatorCardModal;
